import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import isoWeek from "dayjs/plugin/isoWeek";
import "dayjs/locale/fr";
import {
  KpiBatchInfoItem,
  KpiBatchSynthesis,
  BatchPeriodicity,
  BatchExecutionFreshness,
} from "../types/kpiBatchInfo";

dayjs.extend(relativeTime);
dayjs.extend(isoWeek);

export const RANGE_LABEL_MAP: Record<string, string> = {
  DIRECT: "Direct",
  PREVIOUSDAY: "J-1",
  CURRENTWEEK: "Sem. en cours",
  LASTWEEK: "S-1",
  CURRENTMONTH: "Mois en cours",
  LASTMONTH: "M-1",
  TODAY: "Aujourd'hui",
  NEXTDAY: "Demain",
  DIRECTLASTYEAR: "Direct N-1",
  PREVIOUSDAYLASTYEAR: "J-1 N-1",
  CURRENTWEEKLASTYEAR: "Sem. N-1",
  LASTWEEKLASTYEAR: "S-1 N-1",
  CURRENTMONTHLASTYEAR: "Mois N-1",
  LASTMONTHLASTYEAR: "M-1 N-1",
};

/**
 * Returns a human-friendly label for a KPI range code.
 */
export const formatRangeLabel = (range: string | undefined | null): string => {
  if (!range) return "—";
  const upper = range.toUpperCase().trim();
  return RANGE_LABEL_MAP[upper] || range;
};

/**
 * Determines the target periodicity ('day' | 'week' | 'month' | 'year')
 * from a KPI range/temporality string.
 */
export const getRangePeriodicity = (
  range: string | null | undefined
): BatchPeriodicity => {
  if (!range) return "day";
  const upper = range.toUpperCase().trim();
  // Strip comparison suffixes like LASTYEAR or N-1 to assess the base periodicity
  const baseRange = upper.replace(/LASTYEAR|LAST_YEAR|N-1/g, "");

  // Month-based ranges (MONTH, MOIS, M-1, MENSUEL)
  if (
    baseRange.includes("MONTH") ||
    baseRange.includes("MOIS") ||
    baseRange.includes("MENSUEL") ||
    baseRange.includes("M-1")
  ) {
    return "month";
  }

  // Week-based ranges (WEEK, SEMAINE, S-1, HEBDO)
  if (
    baseRange.includes("WEEK") ||
    baseRange.includes("SEMAINE") ||
    baseRange.includes("HEBDO") ||
    baseRange.includes("S-1")
  ) {
    return "week";
  }

  // Year-based ranges (YEAR, ANNEE, ANNUEL)
  if (
    baseRange.includes("YEAR") ||
    baseRange.includes("ANNEE") ||
    baseRange.includes("ANNUEL")
  ) {
    return "year";
  }

  // Default fallback to day (covers DIRECT, PREVIOUSDAY, TODAY, DAY, JOUR, "en cours", etc.)
  return "day";
};

const PERIOD_LABELS: Record<
  BatchPeriodicity,
  { label: string; expected: string; missing: string }
> = {
  day: {
    label: "jour",
    expected: "Dernière exécution attendue aujourd'hui",
    missing: "Non exécuté aujourd'hui",
  },
  week: {
    label: "semaine",
    expected: "Dernière exécution attendue cette semaine",
    missing: "Non exécuté cette semaine",
  },
  month: {
    label: "mois",
    expected: "Dernière exécution attendue ce mois-ci",
    missing: "Non exécuté ce mois-ci",
  },
  year: {
    label: "année",
    expected: "Dernière exécution attendue cette année",
    missing: "Non exécuté cette année",
  },
};

/**
 * Checks if the KPI batch execution is up-to-date according to its temporality.
 * For daily/direct: runAt must be on the same day.
 * For weekly: runAt must be in the same week (ISO week).
 * For monthly: runAt must be in the same month.
 * For yearly: runAt must be in the same year.
 */
export const checkBatchFreshness = (
  runAt: string | null | undefined,
  range: string | null | undefined,
  referenceDate: string | Date | dayjs.Dayjs = new Date()
): BatchExecutionFreshness => {
  const periodicity = getRangePeriodicity(range);
  const now = dayjs(referenceDate);
  const config = PERIOD_LABELS[periodicity];

  if (!runAt || !runAt.trim()) {
    return {
      isWarning: true,
      periodicity,
      periodLabel: config.label,
      message: `Date d'exécution manquante (${config.expected.toLowerCase()})`,
      shortMessage: "Date manquante",
    };
  }

  const run = dayjs(runAt);
  if (!run.isValid()) {
    return {
      isWarning: true,
      periodicity,
      periodLabel: config.label,
      message: "Date d'exécution invalide",
      shortMessage: "Date invalide",
    };
  }

  let isSamePeriod = false;
  switch (periodicity) {
    case "day":
      isSamePeriod = run.isSame(now, "day");
      break;
    case "week":
      isSamePeriod = run.isSame(now, "isoWeek");
      break;
    case "month":
      isSamePeriod = run.isSame(now, "month");
      break;
    case "year":
      isSamePeriod = run.isSame(now, "year");
      break;
  }

  const isWarning = !isSamePeriod;

  return {
    isWarning,
    periodicity,
    periodLabel: config.label,
    message: isWarning ? config.expected : "Synchronisation à jour",
    shortMessage: isWarning ? config.missing : "À jour",
  };
};

/**
 * Formats a date string into full formatted datetime and relative human time.
 */
export const formatBatchDate = (
  dateString: string | null | undefined
): { formatted: string; relative: string } => {
  if (!dateString) {
    return { formatted: "—", relative: "" };
  }

  const d = dayjs(dateString);
  if (!d.isValid()) {
    return { formatted: dateString, relative: "" };
  }

  return {
    formatted: d.format("DD/MM/YYYY HH:mm:ss"),
    relative: d.locale("fr").fromNow(),
  };
};

/**
 * Computes overall and category-level synthesis statistics for KPI batch runs.
 */
export const computeBatchSynthesis = (
  items: KpiBatchInfoItem[] | undefined | null,
  referenceDate: string | Date | dayjs.Dayjs = new Date()
): KpiBatchSynthesis => {
  if (!items || items.length === 0) {
    return {
      total: 0,
      successCount: 0,
      errorCount: 0,
      warningCount: 0,
      successRate: 0,
      lastRunAt: null,
      categories: {},
    };
  }

  let total = items.length;
  let successCount = 0;
  let errorCount = 0;
  let warningCount = 0;
  let lastRunTimestamp = 0;
  let lastRunAt: string | null = null;

  const categories: KpiBatchSynthesis["categories"] = {};

  for (const item of items) {
    const isSuccess = item.status === "SUCCESS";
    if (isSuccess) {
      successCount++;
    } else {
      errorCount++;
    }

    const freshness = checkBatchFreshness(item.runAt, item.range, referenceDate);
    if (freshness.isWarning) {
      warningCount++;
    }

    if (item.runAt) {
      const time = new Date(item.runAt).getTime();
      if (!isNaN(time) && time > lastRunTimestamp) {
        lastRunTimestamp = time;
        lastRunAt = item.runAt;
      }
    }

    const catKey = item.kpiCategory || "AUTRE";
    if (!categories[catKey]) {
      categories[catKey] = {
        category: catKey,
        total: 0,
        successCount: 0,
        errorCount: 0,
        warningCount: 0,
        lastRunAt: null,
      };
    }

    const cat = categories[catKey];
    cat.total++;
    if (isSuccess) {
      cat.successCount++;
    } else {
      cat.errorCount++;
    }
    if (freshness.isWarning) {
      cat.warningCount++;
    }

    if (item.runAt) {
      const catTime = cat.lastRunAt ? new Date(cat.lastRunAt).getTime() : 0;
      const itemTime = new Date(item.runAt).getTime();
      if (!isNaN(itemTime) && itemTime > catTime) {
        cat.lastRunAt = item.runAt;
      }
    }
  }

  const successRate = total > 0 ? Math.round((successCount / total) * 1000) / 10 : 0;

  return {
    total,
    successCount,
    errorCount,
    warningCount,
    successRate,
    lastRunAt,
    categories,
  };
};

